import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import SeoMeta from "@/components/SeoMeta";
import ScienceNav from "./ScienceNav";
import { ArrowLeft, ArrowRight, BookOpen, Globe, LinkSimple, Question, Warning } from "@phosphor-icons/react";

const LEVEL_TABS = [
  { key: "curious", label: "Curious", desc: "For young learners & beginners" },
  { key: "student", label: "Student", desc: "School level" },
  { key: "advanced", label: "Advanced", desc: "University level" },
  { key: "expert", label: "Expert", desc: "Research context" },
];

const EVIDENCE_STYLE = {
  "well-established": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "strong-evidence": "bg-teal-50 text-teal-800 border-teal-200",
  "active-research": "bg-sky-50 text-sky-800 border-sky-200",
  "uncertain": "bg-amber-50 text-amber-800 border-amber-200",
  "speculative": "bg-violet-50 text-violet-800 border-violet-200",
};

/** Concept page — the heart of Science Pulse. One concept, four depth levels. */
export default function ScienceConcept() {
  const { conceptId } = useParams();
  const [params] = useSearchParams();
  const [concept, setConcept] = useState(null);
  const [missing, setMissing] = useState(false);
  const [level, setLevel] = useState("curious");
  const lang = params.get("lang") || "en";

  useEffect(() => {
    setConcept(null); setMissing(false);
    http.get(`/science/concepts/${conceptId}`, { params: lang !== "en" ? { lang } : {} })
      .then(({ data }) => {
        setConcept(data.concept);
        const lv = data.concept.levels || {};
        if (!lv.curious && lv.student) setLevel("student");
      })
      .catch((e) => { if (e?.response?.status === 404) setMissing(true); });
  }, [conceptId, lang]);

  if (missing) {
    return (
      <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="science-concept-missing">
        <SeoMeta title="Concept not published yet" noindex />
        <div className="max-w-[900px] mx-auto px-5 md:px-10">
          <ScienceNav />
          <div className="rounded-2xl border border-black/10 bg-white p-8 text-center">
            <Question size={30} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)]" />
            <h1 className="mt-2 font-serif-display text-3xl">Not written yet.</h1>
            <p className="mt-2 text-sm text-[#3A3830]">This concept is on the Science Pulse map, but the article hasn't been published. Check back soon — the map grows concept by concept.</p>
            <Link to="/science" className="mt-4 inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]"><ArrowLeft size={11} weight="bold" /> Back to Science Pulse</Link>
          </div>
        </div>
      </main>
    );
  }
  if (!concept) {
    return <main className="min-h-screen pt-40 bg-[color:var(--np-bg,#F5EBDC)]" data-testid="science-concept-loading"><div className="text-center font-mono-tech text-[10px] tracking-[0.3em] uppercase">Loading…</div></main>;
  }

  const levels = concept.levels || {};
  const bodyText = [concept.quick_answer, levels[level]].filter(Boolean).join(". ");
  const pillar = concept.pillar;

  return (
    <main className="min-h-screen pt-32 md:pt-36 pb-24 bg-[color:var(--np-bg,#F5EBDC)] text-[#1B4332]" data-testid="science-concept-page">
      <div className="max-w-[900px] mx-auto px-5 md:px-10">
        <ScienceNav />
        <SeoMeta
          title={concept.title}
          description={concept.short_definition || concept.quick_answer || `${concept.title} — explained at four levels on Science Pulse.`}
          canonical={`${window.location.origin}/science/concept/${concept.concept_id || conceptId}`}
          ogType="article"
          jsonLd={{
            "@context": "https://schema.org",
            "@type": "Article",
            headline: concept.title,
            description: concept.short_definition || concept.quick_answer || undefined,
            url: `${window.location.origin}/science/concept/${concept.concept_id || conceptId}`,
            about: pillar ? pillar.replace(/-/g, " ") : undefined,
          }}
        />
        <Link to={pillar ? `/science/${pillar}` : "/science"} className="inline-flex items-center gap-1 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)] mb-3">
          <ArrowLeft size={11} weight="bold" /> {pillar ? pillar.replace(/-/g, " ") : "Science"}
        </Link>

        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center rounded-full border px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase ${EVIDENCE_STYLE[concept.evidence_label] || EVIDENCE_STYLE["well-established"]}`} data-testid="concept-evidence">
            {String(concept.evidence_label || "").replace("-", " ")}
          </span>
          <span className="inline-flex items-center rounded-full border border-black/10 bg-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase text-[color:var(--np-muted,#3A3830)]">
            {String(concept.content_type || "concept").replace("-", " ")}
          </span>
          {concept.lang !== "en" && (
            <span className="inline-flex items-center gap-1 rounded-full border border-black/10 bg-white px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.2em] uppercase">
              <Globe size={10} /> {concept.lang}
            </span>
          )}
        </div>

        <h1 className="mt-2 font-serif-display text-4xl md:text-5xl tracking-tight" data-testid="concept-title">{concept.title}</h1>
        {concept.scientific_name && <div className="mt-1 italic text-[#3A3830]">{concept.scientific_name}</div>}

        {concept.quick_answer && (
          <div className="mt-4 rounded-2xl bg-[#1B4332] text-[#F5EBDC] p-5" data-testid="concept-quick-answer">
            <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#E4A15C] mb-1.5">Quick answer</div>
            <p className="text-base leading-relaxed">{concept.quick_answer}</p>
          </div>
        )}

        {/* Language + listen tools */}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <ListenTranslateBar text={bodyText} testidPrefix="concept-tools" />
          {(concept.available_languages || []).length > 1 && (
            <div className="inline-flex items-center gap-1.5" data-testid="concept-language-picker">
              <Globe size={13} className="text-[color:var(--np-muted,#3A3830)]" />
              {(concept.available_languages || []).map((l) => (
                <Link key={l} to={`/science/concept/${conceptId}${l === "en" ? "" : `?lang=${l}`}`}
                      data-testid={`concept-lang-${l}`}
                      className={`rounded-full px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.18em] uppercase border ${l === concept.lang ? "bg-[#1B4332] text-[#F5EBDC] border-transparent" : "border-black/15 hover:bg-black/5"}`}>
                  {l}
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Level tabs — the core multi-level system */}
        <div className="mt-6" data-testid="concept-levels">
          <div className="flex flex-wrap gap-1.5">
            {LEVEL_TABS.map((t) => levels[t.key] ? (
              <button
                key={t.key}
                onClick={() => setLevel(t.key)}
                data-testid={`concept-level-${t.key}`}
                aria-pressed={level === t.key}
                className={`rounded-full px-4 py-1.5 font-mono-tech text-[10px] tracking-[0.2em] uppercase border transition-colors ${
                  level === t.key ? "bg-[color:var(--np-brand,#B85F2A)] text-white border-transparent" : "bg-white border-black/15 hover:bg-black/5"
                }`}
              >{t.label}</button>
            ) : null)}
          </div>
          <div className="mt-1 text-xs text-[#3A3830]/70">{LEVEL_TABS.find((t) => t.key === level)?.desc}</div>
          <div className="mt-3 rounded-2xl bg-white border border-black/10 p-6" data-testid="concept-level-body">
            <p className="text-base md:text-lg leading-relaxed text-[#232019]">{levels[level]}</p>
          </div>
        </div>

        {/* Key terms */}
        {concept.key_terms?.length > 0 && (
          <div className="mt-8" data-testid="concept-key-terms">
            <h2 className="font-serif-display text-2xl mb-3 flex items-center gap-2"><BookOpen size={18} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> Key terms</h2>
            <dl className="grid gap-2 sm:grid-cols-2">
              {concept.key_terms.map((kt) => (
                <div key={kt.term} className="rounded-xl bg-white border border-black/10 p-3.5">
                  <dt className="font-medium text-sm">{kt.term}</dt>
                  <dd className="text-sm text-[#3A3830]">{kt.definition}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* Misconceptions */}
        {concept.misconceptions?.length > 0 && (
          <div className="mt-8 rounded-2xl border border-amber-300 bg-amber-50 p-5" data-testid="concept-misconceptions">
            <h2 className="font-serif-display text-xl flex items-center gap-2 text-amber-900"><Warning size={16} weight="fill" /> Common misconceptions</h2>
            <ul className="mt-2 space-y-1.5 text-sm text-amber-900/90 list-disc pl-5">
              {concept.misconceptions.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Knowledge graph links */}
        {(concept.prerequisites?.length > 0 || concept.related?.length > 0) && (
          <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="concept-graph">
            {concept.prerequisites?.length > 0 && (
              <div className="rounded-2xl bg-white border border-black/10 p-5">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">Understand these first</div>
                <div className="flex flex-wrap gap-1.5">
                  {concept.prerequisites.map((p) => (
                    <Link key={p} to={`/science/concept/${p}`} data-testid={`concept-prereq-${p}`} className="inline-flex items-center gap-1 rounded-full border border-black/15 px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase hover:bg-black/5">
                      {p.replace(/-/g, " ")}
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {concept.related?.length > 0 && (
              <div className="rounded-2xl bg-white border border-black/10 p-5">
                <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">Connected concepts</div>
                <div className="flex flex-wrap gap-1.5">
                  {concept.related.map((r) => (
                    <Link key={r} to={`/science/concept/${r}`} data-testid={`concept-related-${r}`} className="inline-flex items-center gap-1 rounded-full bg-[color:var(--np-brand,#B85F2A)]/10 text-[color:var(--np-brand,#B85F2A)] px-2.5 py-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase hover:bg-[color:var(--np-brand,#B85F2A)]/20">
                      {r.replace(/-/g, " ")} <ArrowRight size={9} weight="bold" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Sources */}
        <div className="mt-8 rounded-2xl bg-white border border-black/10 p-5" data-testid="concept-sources">
          <div className="font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-muted,#3A3830)] mb-2">Sources & further reading</div>
          <ul className="space-y-1 text-sm text-[#3A3830]">
            {concept.wikipedia_url && (
              <li className="flex items-center gap-1.5">
                <LinkSimple size={12} />
                <a href={concept.wikipedia_url} target="_blank" rel="noopener noreferrer" data-testid="concept-wikipedia" className="text-[color:var(--np-brand,#B85F2A)] hover:underline">Wikipedia — {concept.title}</a>
              </li>
            )}
            {(concept.sources || []).map((s) => <li key={s}>· {s}</li>)}
          </ul>
          <div className="mt-3 text-xs text-[#3A3830]/60">
            Last reviewed {concept.last_reviewed || "—"} · Level {LEVEL_TABS.findIndex((t) => t.key === level) + 1} of 4 shown above
          </div>
        </div>
      </div>
    </main>
  );
}
